// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import gifStyles from '@app/features/channel/components/GifPicker.module.css';
import {safePause, safePlay, useGifVideoPool} from '@app/features/channel/components/GifVideoPool';
import styles from '@app/features/channel/components/MemesPicker.module.css';
import {formatDuration, getFileExtension} from '@app/features/channel/components/pickers/memes/MediaFormat';
import {usePooledVideo} from '@app/features/channel/components/pickers/shared/usePooledVideo';
import MemesPicker from '@app/features/emoji/state/MemesPicker';
import * as FavoriteMemeCommands from '@app/features/expressions/commands/FavoriteMemeCommands';
import {EditFavoriteMemeModal} from '@app/features/expressions/components/modals/EditFavoriteMemeModal';
import type {FavoriteMeme} from '@app/features/expressions/models/FavoriteMeme';
import {ComponentDispatch} from '@app/features/platform/utils/ComponentBus';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {useLingui} from '@lingui/react/macro';
import {MusicNoteIcon, PencilSimpleIcon, TrashIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useRef} from 'react';

const GifIndicator = observer(() => (
	<div className={styles.gifBadge} aria-hidden="true">
		GIF
	</div>
));
export const MemeGridItem = observer(
	({
		meme,
		coords,
		onClose,
		gifAutoPlay,
		isFocused = false,
		itemKey,
	}: {
		meme: FavoriteMeme;
		coords: {
			position: 'absolute' | 'sticky';
			left?: number;
			right?: number;
			width: number;
			top?: number;
			height: number;
		};
		onClose?: () => void;
		gifAutoPlay: boolean;
		isFocused?: boolean;
		itemKey?: string;
	}) => {
		const {t, i18n} = useLingui();
		const videoContainerRef = useRef<HTMLDivElement>(null);
		const videoPool = useGifVideoPool();
		const isAudio = meme.contentType.startsWith('audio/');
		const isVideo = meme.contentType.startsWith('video/');
		const videoRef = usePooledVideo({
			src: !isAudio && isVideo ? meme.url : null,
			containerRef: videoContainerRef,
			videoPool,
			autoPlay: gifAutoPlay,
			enabled: !isAudio && isVideo,
		});
		const onMouseEnter = () => {
			if (gifAutoPlay) return;
			const v = videoRef.current;
			if (!v) return;
			void safePlay(v);
		};
		const onMouseLeave = () => {
			if (gifAutoPlay) return;
			const v = videoRef.current;
			if (!v) return;
			safePause(v);
		};
		const handleClick = (event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
			const shiftKey = 'shiftKey' in event ? event.shiftKey : false;
			MemesPicker.trackMemeUsage(meme.id);
			ComponentDispatch.dispatch('FAVORITE_MEME_SELECT', {meme, autoSend: !shiftKey});
			if (!shiftKey) onClose?.();
		};
		const handleEdit = (event: React.MouseEvent) => {
			event.stopPropagation();
			ModalCommands.push(modal(() => <EditFavoriteMemeModal meme={meme} />));
		};
		const handleDelete = (event: React.MouseEvent) => {
			event.stopPropagation();
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={t`Delete Saved Media`}
						description={t`Are you sure you want to delete "${meme.name}"? This action cannot be undone.`}
						primaryText={t`Delete`}
						primaryVariant="danger-primary"
						onPrimary={() => {
							FavoriteMemeCommands.deleteFavoriteMeme(i18n, meme.id);
						}}
					/>
				)),
			);
		};
		return (
			<div
				role="button"
				tabIndex={0}
				className={clsx(gifStyles.gridItem, gifStyles.gridItemGif, isFocused && gifStyles.gridItemFocused)}
				onClick={handleClick}
				onKeyDown={(event) => {
					if (event.key !== 'Enter' && event.key !== ' ') return;
					event.preventDefault();
					handleClick(event);
				}}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				style={coords}
				data-grid-item={itemKey}
				aria-label={t`Send saved media ${meme.name}`}
			>
				{meme.isGifv && <GifIndicator />}
				<div className={gifStyles.gifMediaContainer}>
					{!isAudio && isVideo && <div ref={videoContainerRef} className={gifStyles.gifVideoContainer} />}
					{!isAudio && !isVideo && <img className={gifStyles.gif} src={meme.url} alt={meme.name} loading="lazy" />}
					{isAudio && (
						<div className={styles.audioCard}>
							<MusicNoteIcon className={styles.audioIcon} />
							<div className={styles.audioMeta}>
								{meme.duration && <div className={styles.audioDuration}>{formatDuration(meme.duration)}</div>}
								<Tooltip text={meme.filename}>
									<div className={styles.audioFilename}>{meme.filename}</div>
								</Tooltip>
								<div className={styles.audioBadge}>{getFileExtension(meme.filename, meme.contentType)}</div>
							</div>
						</div>
					)}
				</div>
				<div className={gifStyles.gridItemBackdrop} />
				<div className={gifStyles.hoverActionButtons}>
					<Tooltip text={t`Edit media`} position="bottom">
						<FocusRing offset={-2}>
							<button
								type="button"
								onClick={handleEdit}
								className={gifStyles.favoriteButton}
								aria-label={t`Edit Saved Media`}
							>
								<PencilSimpleIcon className={gifStyles.favoriteButtonIcon} weight="fill" />
							</button>
						</FocusRing>
					</Tooltip>
					<Tooltip text={t`Delete media`} position="bottom">
						<FocusRing offset={-2}>
							<button
								type="button"
								onClick={handleDelete}
								className={clsx(gifStyles.favoriteButton, gifStyles.favoriteButtonDanger)}
								aria-label={t`Delete Saved Media`}
							>
								<TrashIcon className={gifStyles.favoriteButtonIcon} weight="fill" />
							</button>
						</FocusRing>
					</Tooltip>
				</div>
			</div>
		);
	},
);
